/**
 * NMS Size Selection Modal Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles size selection modal with variant picker and checkout integration
 */

class NMSSizeSelectionModal extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.modalId = element.id || `size-modal-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from Liquid
    this.productId = element.dataset.productId;
    this.formId = element.dataset.formId || 'product-form-main';

    // REQUIRED: Store in global registry for external access
    window.neemans = window.neemans || {};
    window.neemans.sizeModal = window.neemans.sizeModal || {};
    window.neemans.sizeModal[this.modalId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupModalRefs();
    this.#initializeModalState();
    this.#bindModalEvents();
    this.#bindVariantEvents();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupModalRefs() {
    // Query only within component scope
    this.refs.backdrop = this.element.querySelector('.nms-modal__backdrop');
    this.refs.closeButton = this.element.querySelector('.nms-modal__close');
    this.refs.variantInputs = this.element.querySelectorAll('input[data-option-position]');
    this.refs.sizeSelection = this.element.querySelector('.nms-size-selection');
  }

  #initializeModalState() {
    // Set up global data in window.neemans namespace - use a different path to avoid conflicts
    this.setGlobalData(`sizeModalData.${this.modalId}`, {
      isOpen: false,
      selectedVariant: null,
      isLoading: false,
    });

    // Ensure no variants are selected by default
    this.#clearAllSelections();
  }

  #bindModalEvents() {
    // Backdrop click to close
    if (this.refs.backdrop) {
      const backdropHandler = event => {
        if (event.target === this.refs.backdrop) {
          this.close();
        }
      };
      this.refs.backdrop.addEventListener('click', backdropHandler);
      this.#eventListeners.push({ element: this.refs.backdrop, event: 'click', handler: backdropHandler });
    }

    // Close button click
    if (this.refs.closeButton) {
      const closeHandler = event => {
        event.preventDefault();
        this.close();
      };
      this.refs.closeButton.addEventListener('click', closeHandler);
      this.#eventListeners.push({ element: this.refs.closeButton, event: 'click', handler: closeHandler });
    }

    // ESC key handler
    const escHandler = event => {
      if (event.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    };
    document.addEventListener('keydown', escHandler);
    this.#eventListeners.push({ element: document, event: 'keydown', handler: escHandler });
  }

  #bindVariantEvents() {
    // Bind variant option change events
    for (const input of this.refs.variantInputs) {
      const handler = event => this.#handleVariantChange(event);
      input.addEventListener('change', handler);
      this.#eventListeners.push({ element: input, event: 'change', handler });
    }
  }

  #handleVariantChange(event) {
    const input = event.target;
    if (!input.checked) return;

    try {
      // Update visual state for selected variant within modal only
      this.#updateSelectedStates(input);

      // Store selection internally but don't update main buybox
      const variantData = this.#getSelectedVariantData();
      this.setGlobalData(`sizeModalData.${this.modalId}.selectedVariant`, variantData);

      // Call onBuyNowFunc when variant is selected
      this.#callOnBuyNowFunc();

      // DO NOT dispatch variant change event to avoid updating main buybox
      // DO NOT close modal - user can change their selection
    } catch (error) {
      this.handleError(error, 'Failed to handle variant change');
    }
  }

  #getSelectedVariantData() {
    const selectedOptions = {};

    for (const input of this.refs.variantInputs) {
      if (input.checked) {
        const position = input.dataset.optionPosition;
        const value = this.#getOptionDisplayValue(input);
        selectedOptions[position] = {
          id: input.value,
          value: value,
          position: position,
        };
      }
    }

    return selectedOptions;
  }

  #getOptionDisplayValue(input) {
    // Extract display value from label or associated text
    const label = input.nextElementSibling || input.closest('label');
    if (label) {
      const textElement = label.querySelector('.nms-size-selection__value-text, .nms-size-selection__swatch-text');
      if (textElement) {
        return textElement.textContent.trim();
      }
      return label.textContent.trim();
    }
    return input.value;
  }

  #updateSelectedStates(selectedInput) {
    // Clear all selected states first (single selection)
    for (const input of this.refs.variantInputs) {
      const label = input.nextElementSibling || input.closest('label');
      if (label) {
        label.classList.remove('is-selected', 'block-swatch--active');
      }
    }

    // Add selected state to the currently selected option
    if (selectedInput.checked) {
      const selectedLabel = selectedInput.nextElementSibling || selectedInput.closest('label');
      if (selectedLabel) {
        selectedLabel.classList.add('is-selected', 'block-swatch--active');
      }
    }
  }

  #clearAllSelections() {
    // Uncheck all variant inputs and clear visual states
    for (const input of this.refs.variantInputs) {
      input.checked = false;
      const label = input.nextElementSibling || input.closest('label');
      if (label) {
        label.classList.remove('is-selected', 'block-swatch--active');
      }
    }
  }

  onBuyNowFunc(e) {
    let variantId = null;
    for (const input of this.refs.variantInputs) {
      if (input.checked) {
        variantId = input.dataset.variantId;
        break;
      }
    }

    if (!variantId) {
      variantId = ShopifyAnalytics.meta.selectedVariantId;
    }

    let qty = document.querySelector('input[name="quantity"]').value;
    
    // Get GST properties from the product form
    const properties = this.#getGSTProperties();

    onBuyNowClick(e, false, [{ id: variantId, quantity: qty, properties: properties }]);
  }

  #callOnBuyNowFunc() {
    try {
      if (typeof onBuyNowClick !== 'function') {
        setTimeout(() => {
          if (typeof onBuyNowClick === 'function') {
            this.#callOnBuyNowFunc();
          }
        }, 100);
        return;
      }

      const mockEvent = new Event('click');
      this.onBuyNowFunc(mockEvent);
    } catch (error) {
      console.error('Error calling onBuyNowFunc:', error);
    }
  }

  /**
   * Get GST properties from the product form
   */
  #getGSTProperties() {
    const properties = {};

    // Find the product form
    const productForm = document.querySelector('product-form');

    if (productForm) {
      // Get GST rate reduction value
      const gstRateInput = productForm.querySelector('input[name="properties[gst_rate_reduction]"]');
      if (gstRateInput && gstRateInput.value) {
        properties.gst_rate_reduction = gstRateInput.value;
      }

      // Get original price (compare_at_price)
      const originalPriceInput = productForm.querySelector('input[name="properties[original_price]"]');
      if (originalPriceInput && originalPriceInput.value) {
        properties.original_price = originalPriceInput.value;
      }
    }

    return properties;
  }

  // Public API methods
  open() {
    this.element.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Update global state
    this.setGlobalData(`sizeModalData.${this.modalId}.isOpen`, true);

    // Dispatch modal open event
    this.dispatchNMSEvent('size-modal:opened', {
      modalId: this.modalId,
      component: this,
    });
  }

  close() {
    this.element.style.display = 'none';
    document.body.style.overflow = '';

    // Update global state
    this.setGlobalData(`sizeModalData.${this.modalId}.isOpen`, false);

    // Dispatch modal close event
    this.dispatchNMSEvent('size-modal:closed', {
      modalId: this.modalId,
      component: this,
    });
  }

  isOpen() {
    return this.getGlobalData(`sizeModalData.${this.modalId}.isOpen`) || false;
  }

  getSelectedVariant() {
    return this.getGlobalData(`sizeModalData.${this.modalId}.selectedVariant`);
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.sizeModal?.[this.modalId]) {
      delete window.neemans.sizeModal[this.modalId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSSizeSelectionModal() {
  if (!window.NMSBaseClass) {
    console.error('NMSSizeSelectionModal: NMSBaseClass required');
    return;
  }

  // Find all size selection modal components
  const elements = document.querySelectorAll('[data-nms-size-modal]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSSizeSelectionModal(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSizeSelectionModal);
} else {
  initNMSSizeSelectionModal();
}

// Handle custom buy button clicks to open size modal
document.addEventListener('click', function (event) {
  const trigger = event.target.closest('[data-open-size-modal]');
  if (trigger) {
    event.preventDefault();
    const modalId = trigger.dataset.openSizeModal;
    const modal = document.getElementById(modalId);
    if (modal && modal.hasAttribute('data-nms-size-modal')) {
      const instance = window.neemans.sizeModal?.[modalId];
      if (instance) {
        instance.open();
      }
    }
  }
});

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-size-modal]')) {
    initNMSSizeSelectionModal();
  }
});

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;

  // Find size modal elements in the updated container and reinitialize
  const sizeModalElements = container.querySelectorAll('[data-nms-size-modal]');

  for (const element of sizeModalElements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSSizeSelectionModal(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSSizeSelectionModal = NMSSizeSelectionModal;

// Global initialization function for other components to use
window.initNMSSizeSelectionModal = initNMSSizeSelectionModal;

// Legacy global functions for backwards compatibility
window.openSizeSelectionModal = function () {
  const modal = document.getElementById('nms-size-selection-modal');
  if (modal && modal.hasAttribute('data-nms-size-modal')) {
    const modalId = modal.id;
    const instance = window.neemans.sizeModal?.[modalId];
    if (instance) {
      instance.open();
    }
  }
};

window.closeSizeSelectionModal = function () {
  const modal = document.getElementById('nms-size-selection-modal');
  if (modal && modal.hasAttribute('data-nms-size-modal')) {
    const modalId = modal.id;
    const instance = window.neemans.sizeModal?.[modalId];
    if (instance) {
      instance.close();
    }
  }
};
